import { Link, matchPath, useLocation } from "react-router-dom";

import { AppWhiteLogoIcon } from "components/svg-icons";
import AccountMenu from "components/account-menu";
import {
  ProjectContext,
  TProjectContext,
  isPermissionNotGrantted
} from "context/ProjectProvider";
import { useContext, useEffect, useMemo, useState } from "react";
import { TruncatedTextWithTooltip } from "components/cell-renders";
import {
  RoleSubscriptionEnum,
  SubscriptionContext
} from "context/SubscriptionProvider";
import { matchUserProjectRole } from "utils/utils";
import { Popover, Tooltip } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { DATE_FORMAT_MMDDYYYY_HHMM_A_Z, DateUtils } from "utils/dateutils";
import {
  getProjectHomePath,
  isPreconstructionProjectHome
} from "utils/project-home-path";
// import { useShowDesignAndSpec } from "hooks/common";
import ProjectLogoIcon from "components/svg-icons/project-logo";
import { EUserTypes, ProjectPermissionEnum } from "../../constants";
import styles from "./navbar.module.scss";

type MenuLink = {
  label: any;
  key: string;
  children?: MenuLink[];
};

function NavbarMenuDropdown({
  menuItem,
  isActive
}: {
  menuItem: MenuLink;
  isActive: boolean;
}) {
  const [open, setOpen] = useState(false);
  // Only rendered when `x.children?.length` is truthy (see Navbar map), so menuItem.children is always a non-empty array here; the `?? []` fallback is unreachable.
  /* v8 ignore next */
  const children = menuItem.children ?? [];

  const content = (
    <div className="flex flex-col bg-ciq-header-100 rounded-lg shadow-xl bg-opacity-100">
      {children.map((child, index) => (
        <Link
          key={child.key}
          to={child.key}
          onClick={() => setOpen(false)}
          className={`flex items-center px-5 py-3 space-x-2 cursor-pointer hover:bg-black text-white fill-white hover:text-ciq-golden-purple-500 hover:fill-ciq-golden-purple-500 ${
            index === 0 ? "rounded-t-lg" : ""
          } ${index === children.length - 1 ? "rounded-b-lg" : ""}`}
        >
          {child.label}
        </Link>
      ))}
    </div>
  );

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      content={content}
      classNames={{ root: styles.popoverNavbarNoPadding }}
      placement="bottom"
      // antd v6 renamed `showArrow` to `arrow`; the old prop is ignored, which
      // put a white default-coloured arrow above this dark menu panel.
      arrow={false}
    >
      <div
        className={
          isActive
            ? `group hover:drop-shadow-[0_0_0.3px_rgba(0,0,0,1)] ${styles.menuActiveIconCSS}`
            : `group hover:drop-shadow-[0_0_0.3px_rgba(0,0,0,1)] ${styles.menuIconCSS}`
        }
      >
        <div className="flex items-center space-x-1">
          {menuItem.label}
          <DownOutlined
            className={
              isActive
                ? "text-xs text-ciq-golden-purple-500"
                : "text-xs text-white group-hover:text-ciq-golden-purple-500"
            }
          />
        </div>
      </div>
    </Popover>
  );
}

export function DateTimePanel() {
  const [time, setTime] = useState(DateUtils.dateTimeObj());
  useEffect(() => {
    const timer = setInterval(() => {
      // Creates an interval which will update the current data every minute
      // This will trigger a rerender every component that uses the useDate hook.
      setTime(DateUtils.dateTimeObj());
    }, 1000);
    return () => {
      clearInterval(timer); // Return a funtion to clear the timer so that it will stop being called on unmount
    };
  }, []);
  return (
    <Tooltip
      className="w-40"
      title={time.format(DATE_FORMAT_MMDDYYYY_HHMM_A_Z)}
    >
      {time.format("hh:mmA (z)")}
    </Tooltip>
  );
}

type NavbarProps = {
  menuLinks?: MenuLink[];
};

function Navbar(props: NavbarProps) {
  const { menuLinks = [] } = props;
  const { pathname } = useLocation();

  const {
    subscriptionRole,
    isFeatureFlagEnabled: isFeatureFlagSubscriptionEnabled
  } = useContext(SubscriptionContext);
  const {
    tokenContents,
    projectDetails,
    isFeatureFlagEnabled: isFeatureFlagProjectEnabled
  }: TProjectContext = useContext(ProjectContext);

  const isFeatureFlagEnabled =
    isFeatureFlagProjectEnabled || isFeatureFlagSubscriptionEnabled;

  const isBuyoutFeatureEnabled = isFeatureFlagEnabled("BUYOUT");

  const filteredMenuLinks = useMemo(() => {
    const cannotViewProjectReports = isPermissionNotGrantted(
      ProjectPermissionEnum.ViewProjectReports,
      tokenContents?.role ?? ""
    );
    const cannotViewDesignPage = isPermissionNotGrantted(
      ProjectPermissionEnum.ViewDesignPage,
      tokenContents?.role ?? ""
    );
    const isPreconLink = (key: string) =>
      key.includes("/design-packages") ||
      key.includes("/bid-packages") ||
      key.includes("/design-scope");
    const isConstructionLink = (key: string) =>
      key.includes("/submittals") ||
      key.includes("/materials") ||
      key.includes("/schedule");
    const isPreconstructionStage = isPreconstructionProjectHome(
      projectDetails,
      isBuyoutFeatureEnabled,
      !cannotViewDesignPage
    );
    const showConstructionGroup =
      isPreconstructionStage && isBuyoutFeatureEnabled && !cannotViewDesignPage;

    let fMenuLinks = menuLinks;
    if (projectDetails && cannotViewProjectReports) {
      fMenuLinks = fMenuLinks.filter((item) => {
        return item.label !== "Reports";
      });
    }

    if (!isBuyoutFeatureEnabled || cannotViewDesignPage) {
      fMenuLinks = fMenuLinks
        .map((item) => {
          if (item.key === "construction") {
            return null;
          }
          if (item.children) {
            const filteredChildren = item.children.filter(
              (child) => !isPreconLink(child.key)
            );
            if (filteredChildren.length === 0) {
              return null;
            }
            return { ...item, children: filteredChildren };
          }
          if (isPreconLink(item.key)) {
            return null;
          }
          return item;
        })
        .filter((item): item is MenuLink => item !== null);
    }

    fMenuLinks = fMenuLinks.filter((item) => {
      if (item.key === "precon") {
        return !isPreconstructionStage;
      }
      if (item.key === "construction") {
        return showConstructionGroup;
      }
      if (!item.children && isPreconLink(item.key)) {
        return isPreconstructionStage;
      }
      if (!item.children && isConstructionLink(item.key)) {
        return !showConstructionGroup;
      }
      return true;
    });

    if (subscriptionRole < RoleSubscriptionEnum.subscription_admin) {
      fMenuLinks = fMenuLinks.filter((item) => {
        return item.key !== "/analytics";
      });
    }

    if (subscriptionRole < RoleSubscriptionEnum.subscription_admin) {
      fMenuLinks = fMenuLinks.filter((item) => {
        return item.key !== "/settings";
      });
    }
    return fMenuLinks;
  }, [
    isBuyoutFeatureEnabled,
    menuLinks,
    projectDetails,
    subscriptionRole,
    tokenContents?.role
  ]);

  const isCurrentUserGC = matchUserProjectRole(
    EUserTypes.GENERAL_CONTRACTOR,
    tokenContents?.role
  );

  const isCurrentUserSC = matchUserProjectRole(
    EUserTypes.SUBCONTRACTOR,
    tokenContents?.role
  );

  const isCurrentUserArchitect = matchUserProjectRole(
    EUserTypes.ARCHITECT,
    tokenContents?.role
  );

  const isCurrentUserOwner = matchUserProjectRole(
    EUserTypes.OWNER,
    tokenContents?.role
  );

  const projectHomePath = getProjectHomePath(
    projectDetails?.id ?? "",
    isPreconstructionProjectHome(
      projectDetails,
      isBuyoutFeatureEnabled,
      !isPermissionNotGrantted(
        ProjectPermissionEnum.ViewDesignPage,
        tokenContents?.role ?? ""
      )
    )
  );

  return (
    <div className="flex justify-between items-center gap-9 w-full min-w-0 bg-ciq-primary-200 text-white hover:text-white px-4">
      <div className="flex min-w-0 flex-1">
        <div className="flex shrink-0 items-center border-0 border-r border-solid py-2 pr-3 border-black">
          <Link
            to="/projects"
            className="flex"
            data-testid="navbar-link-app-logo"
          >
            <div className="w-[120px] flex">
              <AppWhiteLogoIcon />
            </div>
          </Link>
        </div>

        {projectDetails?.id &&
          (isCurrentUserGC ||
          isCurrentUserSC ||
          isCurrentUserArchitect ||
          isCurrentUserOwner ? (
            <Link
              to={projectHomePath}
              className="flex min-w-0 items-center space-x-2 text-white hover:text-white pl-3"
              data-testid="navbar-link-project-logo"
            >
              <span className="shrink-0">
                <ProjectLogoIcon size={30} />
              </span>
              <div className="flex min-w-0 items-center space-x-1">
                <div className="font-medium truncate min-w-0">
                  <TruncatedTextWithTooltip
                    text={projectDetails?.name}
                    className="!w-auto"
                  />
                </div>
                <div className="shrink-0">|</div>
                <div className="shrink-0">
                  <DateTimePanel />
                </div>
              </div>
            </Link>
          ) : (
            <div className="min-w-0 truncate pl-3">{projectDetails?.name}</div>
          ))}
      </div>
      <div className="flex shrink-0 space-x-4 items-center h-full">
        <div className="flex h-full whitespace-nowrap">
          {filteredMenuLinks.map((x) => {
            if (x.children?.length) {
              const isActive = x.children.some((child) =>
                matchPath(pathname, { path: child.key })
              );

              return (
                <NavbarMenuDropdown
                  key={x.key}
                  menuItem={x}
                  isActive={isActive}
                />
              );
            }

            return (
              <Link
                key={x.key}
                to={x.key}
                className={
                  matchPath(pathname, { path: x.key })
                    ? `hover:drop-shadow-[0_0_0.3px_rgba(0,0,0,1)] ${styles.menuActiveIconCSS}`
                    : `hover:drop-shadow-[0_0_0.3px_rgba(0,0,0,1)] ${styles.menuIconCSS}`
                }
              >
                {x.label}
              </Link>
            );
          })}
        </div>
        <div className="pl-3 py-2 flex justify-center items-center border-0 border-l border-solid border-black">
          <AccountMenu />
        </div>
      </div>
    </div>
  );
}

export default Navbar;
